import { create }  from 'zustand'
import { api }      from '@/lib/api'

export interface WhiteLabelConfig {
  agencyId:    string
  brandName:   string
  logoUrl:     string | null
  primaryColor: string
  secondaryColor: string
  bgColor:     string
  customDomain: string | null
  faviconUrl:  string | null
  supportEmail: string | null
  hideFooterBranding: boolean
  // Planos que a agência oferece (pode ser subset do Dikis AI)
  availablePlans: string[]
}

interface WhiteLabelState {
  config:    WhiteLabelConfig | null
  isWhiteLabel: boolean
  loading:   boolean

  initWhiteLabel: () => Promise<void>
  applyTheme:     (config: WhiteLabelConfig) => void
  resetTheme:     () => void
}

// Tema padrão Dikis AI
const DEFAULT_THEME = {
  primaryColor:   '#6366f1',
  secondaryColor: '#10b981',
  bgColor:        '#0f172a',
}

export const useWhiteLabelStore = create<WhiteLabelState>()((set) => ({
  config:       null,
  isWhiteLabel: false,
  loading:      false,

  // ── Detecta white-label pelo hostname ────────────────
  initWhiteLabel: async () => {
    const hostname = window.location.hostname

    // Subdomínios do próprio Dikis AI → não é white-label
    const ownDomains = ['dikisai.com.br', 'dikisai.app', 'localhost', '127.0.0.1']
    const isOwnDomain = ownDomains.some(d => hostname === d || hostname.endsWith(`.${d}`))

    if (isOwnDomain) {
      set({ isWhiteLabel: false, loading: false })
      return
    }

    // Tenta buscar config de white-label para esse domínio
    set({ loading: true })
    try {
      const { data } = await api.get<WhiteLabelConfig>(
        `/white-label/config?domain=${hostname}`
      )
      set({ config: data, isWhiteLabel: true })

      // Aplica o tema
      const store = useWhiteLabelStore.getState()
      store.applyTheme(data)
    } catch {
      // Domínio não encontrado → usa tema padrão
      set({ isWhiteLabel: false })
    } finally {
      set({ loading: false })
    }
  },

  // ── Aplica variáveis CSS do white-label ─────────────
  applyTheme: (config) => {
    const root = document.documentElement

    root.style.setProperty('--wl-primary',        config.primaryColor)
    root.style.setProperty('--wl-primary-hover',  adjustColor(config.primaryColor, -15))
    root.style.setProperty('--wl-secondary',      config.secondaryColor)
    root.style.setProperty('--wl-bg',             config.bgColor)
    root.style.setProperty('--wl-brand-name',     `'${config.brandName}'`)

    // Atualiza título da aba
    document.title = config.brandName

    // Atualiza favicon se fornecido
    if (config.faviconUrl) {
      const favicon = document.getElementById('favicon') as HTMLLinkElement
      if (favicon) favicon.href = config.faviconUrl
    }

    set({ config })
  },

  // ── Reseta para tema padrão Dikis AI ─────────────────
  resetTheme: () => {
    const root = document.documentElement
    root.style.setProperty('--wl-primary',   DEFAULT_THEME.primaryColor)
    root.style.setProperty('--wl-secondary', DEFAULT_THEME.secondaryColor)
    root.style.setProperty('--wl-bg',        DEFAULT_THEME.bgColor)
    document.title = 'Dikis AI'
    set({ config: null, isWhiteLabel: false })
  },
}))

// ── Utilitário: escurece/clareia cor hex ─────────────────
function adjustColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.min(255, Math.max(0, (num >> 16) + amount))
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount))
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}
